/**
 * PromotionsTab — admin-only. Lifecycle management for the prize
 * mechanic. Kill-switch is deliberately large and red so it's obvious;
 * "Draw winner" / "End" / "Delete" are irreversible and gated behind a
 * confirmation.
 *
 * Backend contract (see routes/promotions.py):
 *   GET    /api/promotions/                    — list w/ total_discount_given + is_expired
 *   POST   /api/promotions/                    — create in draft
 *   PATCH  /api/promotions/{id}                — edit (draft OR zero-entry only)
 *   POST   /api/promotions/{id}/activate       — draft/paused → active
 *   POST   /api/promotions/{id}/pause          — KILL SWITCH
 *   POST   /api/promotions/{id}/resume         — paused → active (blocked if expired)
 *   POST   /api/promotions/{id}/end            — active/paused → ended (terminal)
 *   POST   /api/promotions/{id}/draw           — single_winner only
 *   DELETE /api/promotions/{id}                — hard-delete, entries_count==0 only
 *   GET    /api/promotions/{id}/entries        — full entry log
 */
import React, { useCallback, useEffect, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Link } from "react-router-dom";
import {
  Gift, Play, Pause, Square, Loader2, RefreshCw, Plus, Trophy, X, Eye,
  Pencil, Trash2, FileText, ExternalLink, RotateCcw,
} from "lucide-react";
import { Button } from "../ui/button";
import { Input } from "../ui/input";
import { Label } from "../ui/label";
import { Textarea } from "../ui/textarea";
import { Card, CardContent } from "../ui/card";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "../ui/select";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle,
  DialogDescription, DialogFooter,
} from "../ui/dialog";
import { API } from "../../App";
import { TermsBlock } from "../PromotionTermsSection";

const STATUS_COLORS = {
  draft: "text-slate-400 bg-slate-800 border-slate-600",
  active: "text-emerald-300 bg-emerald-500/10 border-emerald-500/40",
  paused: "text-amber-300 bg-amber-500/10 border-amber-500/40",
  ended: "text-slate-500 bg-slate-800/50 border-slate-700",
  expired: "text-rose-300 bg-rose-500/10 border-rose-500/40",
};

// Backend stores start_date/end_date as ISO strings — <input type="datetime-local">
// needs "YYYY-MM-DDTHH:MM" (no seconds, no Z, LOCAL time). Strip the
// zone bits carefully so what the admin sees in the picker matches
// what they originally entered.
const isoToLocalInput = (iso) => {
  if (!iso) return "";
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return "";
  const pad = (n) => String(n).padStart(2, "0");
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`;
};

const PromotionsTab = () => {
  const [promos, setPromos] = useState([]);
  const [loading, setLoading] = useState(true);
  const [formOpen, setFormOpen] = useState(false);
  const [editing, setEditing] = useState(null); // promo obj when editing, null when creating
  const [entriesFor, setEntriesFor] = useState(null);
  const [repairPlan, setRepairPlan] = useState(null);
  const [repairBusy, setRepairBusy] = useState(false);
  const [entries, setEntries] = useState([]);
  const [drawTarget, setDrawTarget] = useState(null);
  const [drawSubmitting, setDrawSubmitting] = useState(false);
  const [endTarget, setEndTarget] = useState(null);
  const [endSubmitting, setEndSubmitting] = useState(false);
  // Reopen (ended promo_code campaigns only). `reopenNeeds` carries the
  // machine-readable precondition the backend returned, so the dialog
  // reveals exactly the field that needs a decision instead of showing
  // a dead-end error.
  const [reopenTarget, setReopenTarget] = useState(null);
  const [reopenSubmitting, setReopenSubmitting] = useState(false);
  const [reopenNeeds, setReopenNeeds] = useState([]);
  const [reopenForm, setReopenForm] = useState({
    new_end_date: "", clear_end_date: false,
    new_max_uses: "", clear_max_uses: false,
  });
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [deleteSubmitting, setDeleteSubmitting] = useState(false);
  const [termsPreviewFor, setTermsPreviewFor] = useState(null); // promo obj
  const [termsPreview, setTermsPreview] = useState(null); // rendered preview shape from backend
  const [loadError, setLoadError] = useState(null); // string message when list fetch fails
  // Cached services catalogue — used to render the "Applies to" chips
  // on each campaign card AND to populate the multi-select inside the
  // create/edit dialog. Fetched once at tab mount; if it fails, we
  // still render the tab (chips just fall back to raw IDs).
  const [services, setServices] = useState([]);
  const servicesById = React.useMemo(() => {
    const m = new Map();
    for (const s of services) {
      if (s && s.service_id) m.set(s.service_id, s);
    }
    return m;
  }, [services]);

  const load = useCallback(async () => {
    setLoading(true);
    setLoadError(null);
    try {
      const r = await axios.get(`${API}/promotions/`);
      // HARDENING: reject non-array payloads so a malformed response
      // can't crash .map() later. Missing/invalid → treat as empty
      // AND surface the error state so the admin gets a retry CTA
      // rather than a silently blank tab.
      if (!Array.isArray(r?.data)) {
        throw new Error("Promotions API returned an unexpected shape");
      }
      setPromos(r.data);
    } catch (err) {
      const msg = err.response?.data?.detail || err.message || "Failed to load promotions";
      setLoadError(msg);
      setPromos([]);
      toast.error(msg);
    } finally { setLoading(false); }
  }, []);

  useEffect(() => { load(); }, [load]);

  // One-shot services fetch — cached for card chips + form multiselect.
  // Failures are non-fatal: the campaign list still renders and the
  // dialog falls back to an empty picker with a "couldn't load"
  // notice, letting the admin submit an empty (= all services) list.
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const r = await axios.get(`${API}/services/`);
        if (!cancelled && Array.isArray(r.data)) setServices(r.data);
      } catch (err) {
        // Non-fatal — log for the console but don't block the tab.
        console.warn("[PromotionsTab] services fetch failed:", err?.message);
      }
    })();
    return () => { cancelled = true; };
  }, []);

  const transition = async (promo, path, verb) => {
    try {
      await axios.post(`${API}/promotions/${promo.promotion_id}/${path}`);
      toast.success(`${verb} "${promo.name}"`);
      load();
    } catch (err) {
      toast.error(err.response?.data?.detail || `Failed to ${verb.toLowerCase()}`);
    }
  };

  const openEntries = async (promo) => {
    setEntriesFor(promo);
    setEntries([]);
    setRepairPlan(null);
    try {
      const r = await axios.get(`${API}/promotions/${promo.promotion_id}/entries`);
      setEntries(r.data || []);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to load entries");
    }
  };

  // One-off integrity repair for promo_code campaigns — removes the
  // "ghost" entries the old auto-entry behaviour wrote and backfills
  // real redemptions that were never logged. Dry-run first, always.
  const runEntriesRepair = async (dryRun) => {
    if (!entriesFor) return;
    setRepairBusy(true);
    try {
      const r = await axios.post(
        `${API}/promotions/admin/repair-promo-code-entries` +
        `?dry_run=${dryRun}&promotion_id=${entriesFor.promotion_id}`
      );
      const campaign = (r.data.campaigns || [])[0] || null;
      if (dryRun) {
        setRepairPlan(campaign);
        const t = r.data.totals || {};
        toast.success(
          (t.ghosts + t.repairs + t.backfills) === 0
            ? "Nothing to fix — this list is already accurate"
            : `${t.ghosts} ghost, ${t.repairs} incomplete, ${t.backfills} missing`
        );
      } else {
        toast.success("Entries list fixed");
        setRepairPlan(null);
        const fresh = await axios.get(`${API}/promotions/${entriesFor.promotion_id}/entries`);
        setEntries(fresh.data || []);
        load();
      }
    } catch (err) {
      toast.error(err.response?.data?.detail || "Repair failed");
    } finally {
      setRepairBusy(false);
    }
  };

  const openCreate = () => { setEditing(null); setFormOpen(true); };
  const openEdit = (promo) => { setEditing(promo); setFormOpen(true); };
  const closeForm = () => { setFormOpen(false); setEditing(null); };

  const confirmDraw = async () => {
    if (!drawTarget) return;
    setDrawSubmitting(true);
    try {
      const r = await axios.post(`${API}/promotions/${drawTarget.promotion_id}/draw`);
      toast.success(`Winner drawn — balance now €${r.data.remaining_balance_due}`);
      setDrawTarget(null);
      load();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Draw failed");
    } finally { setDrawSubmitting(false); }
  };

  const confirmEnd = async () => {
    if (!endTarget) return;
    setEndSubmitting(true);
    try {
      await axios.post(`${API}/promotions/${endTarget.promotion_id}/end`);
      toast.success(`Ended "${endTarget.name}"`);
      setEndTarget(null);
      load();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to end");
    } finally { setEndSubmitting(false); }
  };

  const openReopen = (promo) => {
    setReopenTarget(promo);
    setReopenNeeds([]);
    setReopenForm({ new_end_date: "", clear_end_date: false,
                    new_max_uses: "", clear_max_uses: false });
  };

  const confirmReopen = async () => {
    if (!reopenTarget) return;
    setReopenSubmitting(true);
    const body = {};
    if (reopenForm.clear_end_date) body.clear_end_date = true;
    else if (reopenForm.new_end_date) {
      body.new_end_date = new Date(reopenForm.new_end_date).toISOString();
    }
    if (reopenForm.clear_max_uses) body.clear_max_uses = true;
    else if (reopenForm.new_max_uses !== "") {
      body.new_max_uses = Number(reopenForm.new_max_uses);
    }
    try {
      const r = await axios.post(
        `${API}/promotions/${reopenTarget.promotion_id}/reopen`, body);
      toast.success(r.data.message);
      (r.data.warnings || []).forEach((w) => toast.warning(w));
      setReopenTarget(null);
      load();
    } catch (err) {
      const detail = err.response?.data?.detail;
      // A structured detail means "I need one more decision" — keep the
      // dialog open and reveal the field it names.
      if (detail && typeof detail === "object" && detail.code) {
        setReopenNeeds((n) => (n.includes(detail.code) ? n : [...n, detail.code]));
        toast.error(detail.message);
      } else {
        toast.error(typeof detail === "string" ? detail : "Failed to reopen");
      }
    } finally { setReopenSubmitting(false); }
  };

  const confirmDelete = async () => {
    if (!deleteTarget) return;
    setDeleteSubmitting(true);
    try {
      await axios.delete(`${API}/promotions/${deleteTarget.promotion_id}`);
      toast.success(`Deleted "${deleteTarget.name}"`);
      setDeleteTarget(null);
      load();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to delete");
    } finally { setDeleteSubmitting(false); }
  };

  const openTermsPreview = async (promo) => {
    setTermsPreviewFor(promo);
    setTermsPreview(null);
    try {
      const r = await axios.get(`${API}/promotions/${promo.promotion_id}/terms-preview`);
      setTermsPreview(r.data);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to load terms preview");
      setTermsPreviewFor(null);
    }
  };

  return (
    <div className="space-y-6" data-testid="promotions-tab">
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-2xl font-bold text-white flex items-center gap-2">
            <Gift className="w-6 h-6 text-amber-400" />
            Promotions
          </h2>
          <p className="text-slate-400 text-sm mt-1">
            Weighted-random wheel prizes and single-winner jackpots. Kill switch is immediate — takes effect on the very next booking.
          </p>
        </div>
        <div className="flex gap-2">
          <Button variant="outline" size="sm" onClick={load} disabled={loading}
            className="border-slate-700 text-slate-300" data-testid="promotions-refresh-btn">
            {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : <RefreshCw className="w-4 h-4" />}
          </Button>
          <Button size="sm" onClick={openCreate}
            className="bg-amber-500 hover:bg-amber-600 text-slate-900 font-semibold"
            data-testid="promotions-new-btn">
            <Plus className="w-4 h-4 mr-1" /> New campaign
          </Button>
        </div>
      </div>

      {loadError && !loading && (
        <Card className="bg-rose-950/40 border-rose-800" data-testid="promotions-load-error">
          <CardContent className="py-8 text-center">
            <p className="text-rose-200 font-semibold mb-1">Failed to load promotions</p>
            <p className="text-slate-400 text-sm mb-4">{loadError}</p>
            <Button size="sm" onClick={load} disabled={loading}
                    className="bg-rose-500 hover:bg-rose-400 text-white font-semibold"
                    data-testid="promotions-load-retry">
              {loading ? <Loader2 className="w-4 h-4 animate-spin mr-1" /> : <RefreshCw className="w-4 h-4 mr-1" />}
              Try again
            </Button>
          </CardContent>
        </Card>
      )}

      {promos.length === 0 && !loading && !loadError && (
        <Card className="bg-slate-900 border-slate-700">
          <CardContent className="py-10 text-center text-slate-500">
            No campaigns yet. Click <span className="text-amber-300 font-semibold">New campaign</span> to create one.
          </CardContent>
        </Card>
      )}

      <div className="grid gap-3">
        {promos.map((p) => {
          const zeroEntries = (p.entries_count ?? 0) === 0;
          // Editable iff draft OR zero-entry (matches backend rule).
          const editable = p.status === "draft" || zeroEntries;
          // "Live-ish" states where the campaign is still visible to
          // customers or resumable — pause/resume/end apply here.
          const isTerminal = p.status === "ended";
          const displayStatus = p.is_expired ? "expired" : p.status;
          return (
          <Card key={p.promotion_id} className="bg-slate-900 border-slate-700"
                data-testid={`promo-card-${p.promotion_id}`}>
            <CardContent className="pt-4">
              <div className="flex items-start justify-between gap-4">
                <div className="min-w-0">
                  <div className="flex items-center gap-2 flex-wrap">
                    <span className="text-white font-semibold">{p.name}</span>
                    <span className={`text-[10px] uppercase tracking-wide px-2 py-0.5 rounded border ${STATUS_COLORS[displayStatus] || STATUS_COLORS.draft}`}
                          data-testid={`promo-status-${p.promotion_id}`}>
                      {displayStatus}
                    </span>
                    <span className="text-slate-500 text-xs">
                      {p.mechanic_type === "weighted_random" && "Wheel/envelope"}
                      {p.mechanic_type === "single_winner" && "Jackpot"}
                      {p.mechanic_type === "promo_code" && "Promo code"}
                      {p.mechanic_type === "free_wash" && "Free car wash"}
                    </span>
                  </div>
                  <div className="text-xs text-slate-400 mt-1 font-mono">
                    {p.mechanic_type === "free_wash" ? (
                      <span data-testid={`promo-wash-cost-${p.promotion_id}`}>
                        €{Number(p.wash_cost_net ?? 0).toFixed(2)} net @{" "}
                        {Number(p.wash_vat_rate_percent ?? 0)}% VAT · cost per wash
                        {!p.end_date && " · no end date"}
                      </span>
                    ) : p.mechanic_type === "promo_code" ? (
                      <>
                        <span
                          className="text-amber-300 font-semibold"
                          data-testid={`promo-code-value-${p.promotion_id}`}
                        >
                          {p.code || "—"}
                        </span>
                        <span className="text-slate-600 mx-2">·</span>
                        <span data-testid={`promo-uses-${p.promotion_id}`}>
                          {p.uses_count || 0}
                          {p.max_uses != null ? `/${p.max_uses}` : ""} uses
                        </span>
                      </>
                    ) : (
                      <span data-testid={`promo-entries-${p.promotion_id}`}>
                        {p.entries_count}/{p.max_entries} entries
                      </span>
                    )}
                    <span className="text-slate-600 mx-2">·</span>
                    <span data-testid={`promo-discount-given-${p.promotion_id}`}>
                      €{(p.total_discount_given ?? 0).toFixed(2)} given out
                    </span>
                  </div>
                  {/* "Applies to" — visible at a glance so the admin
                      doesn't have to open the edit dialog to check
                      which services the campaign scopes to. Empty /
                      missing = all services (backend interpretation
                      at routes/promotions.py L211-214). */}
                  <div
                    className="text-xs text-slate-400 mt-1"
                    data-testid={`promo-applies-${p.promotion_id}`}
                  >
                    <span className="text-slate-500">Applies to:</span>{" "}
                    {(() => {
                      const ids = Array.isArray(p.applicable_service_ids)
                        ? p.applicable_service_ids
                        : [];
                      if (ids.length === 0) {
                        return <span className="text-slate-300">All services</span>;
                      }
                      const names = ids.map(
                        (sid) => servicesById.get(sid)?.name || sid,
                      );
                      const shown = names.slice(0, 3).join(", ");
                      const extra = names.length - 3;
                      return (
                        <span
                          className="text-slate-300"
                          title={names.join(", ")}
                        >
                          {shown}
                          {extra > 0 ? ` (+${extra} more)` : ""}
                        </span>
                      );
                    })()}
                  </div>
                  {/* Winner pill — visible ONLY for a drawn single_winner
                      campaign. Makes the terminal state obvious like
                      the ENDED badge does; also serves as an
                      unambiguous "no more draws possible" signal. */}
                  {p.winner_entry_id && p.winner_summary && (
                    <div
                      className="mt-2 inline-flex items-center gap-2 text-xs px-2 py-1 rounded border border-amber-500/40 bg-amber-500/10 text-amber-200"
                      data-testid={`promo-winner-${p.promotion_id}`}
                    >
                      <Trophy className="w-3.5 h-3.5 text-amber-300" />
                      <span data-testid={`promo-winner-name-${p.promotion_id}`}>
                        Winner: <strong>{p.winner_summary.customer_name || "—"}</strong>
                      </span>
                      <span className="text-amber-400/60">·</span>
                      <span data-testid={`promo-winner-prize-${p.promotion_id}`}>
                        {p.winner_summary.outcome_label || "Prize"}
                      </span>
                    </div>
                  )}
                </div>
                <div className="flex gap-1 flex-wrap justify-end">
                  {p.status === "draft" && (
                    <Button size="sm" onClick={() => transition(p, "activate", "Activated")}
                            className="bg-emerald-600 hover:bg-emerald-500 text-white"
                            data-testid={`promo-activate-${p.promotion_id}`}>
                      <Play className="w-4 h-4 mr-1" /> Activate
                    </Button>
                  )}
                  {p.status === "active" && (
                    <Button size="sm" onClick={() => transition(p, "pause", "Paused")}
                            className="bg-red-600 hover:bg-red-500 text-white font-semibold"
                            data-testid={`promo-pause-${p.promotion_id}`}>
                      <Pause className="w-4 h-4 mr-1" /> Kill switch
                    </Button>
                  )}
                  {p.status === "paused" && !p.is_expired && (
                    <Button size="sm" onClick={() => transition(p, "resume", "Resumed")}
                            className="bg-emerald-600 hover:bg-emerald-500 text-white"
                            data-testid={`promo-resume-${p.promotion_id}`}>
                      <Play className="w-4 h-4 mr-1" /> Resume
                    </Button>
                  )}
                  {p.can_reopen && (
                    <Button size="sm" onClick={() => openReopen(p)}
                            className="bg-emerald-600 hover:bg-emerald-500 text-white"
                            data-testid={`promo-reopen-${p.promotion_id}`}>
                      <RotateCcw className="w-4 h-4 mr-1" /> Reopen
                    </Button>
                  )}
                  {p.mechanic_type === "single_winner" && !p.winner_entry_id && p.entries_count > 0 && (
                    <Button size="sm" onClick={() => setDrawTarget(p)}
                            className="bg-amber-500 hover:bg-amber-600 text-slate-900 font-semibold"
                            data-testid={`promo-draw-${p.promotion_id}`}>
                      <Trophy className="w-4 h-4 mr-1" /> Draw winner
                    </Button>
                  )}
                  {/* Edit — draft OR zero entries. Shown disabled with tooltip when locked. */}
                  <Button
                    size="sm"
                    variant="outline"
                    onClick={() => editable && openEdit(p)}
                    disabled={!editable}
                    title={editable ? "Edit campaign" : `Locked: ${p.entries_count} entries recorded`}
                    className="border-slate-700 text-slate-300 disabled:opacity-50 disabled:cursor-not-allowed"
                    data-testid={`promo-edit-${p.promotion_id}`}
                  >
                    <Pencil className="w-4 h-4 mr-1" /> Edit
                  </Button>
                  {/* End — terminal, active/paused only. Hidden once ended. */}
                  {!isTerminal && (p.status === "active" || p.status === "paused") && (
                    <Button
                      size="sm"
                      variant="outline"
                      onClick={() => setEndTarget(p)}
                      className="border-slate-700 text-slate-300 hover:text-white"
                      data-testid={`promo-end-${p.promotion_id}`}
                    >
                      <Square className="w-4 h-4 mr-1" /> End
                    </Button>
                  )}
                  {/* Delete — only when zero entries. Otherwise disabled + explain. */}
                  <Button
                    size="sm"
                    variant="outline"
                    onClick={() => zeroEntries && setDeleteTarget(p)}
                    disabled={!zeroEntries}
                    title={zeroEntries ? "Delete campaign" : "Has entries — end it instead"}
                    className="border-red-900/60 text-red-300 hover:text-red-200 hover:bg-red-500/10 disabled:opacity-50 disabled:cursor-not-allowed"
                    data-testid={`promo-delete-${p.promotion_id}`}
                  >
                    <Trash2 className="w-4 h-4 mr-1" /> Delete
                  </Button>
                  <Button size="sm" variant="outline" onClick={() => openTermsPreview(p)}
                          className="border-slate-700 text-slate-300"
                          data-testid={`promo-terms-preview-${p.promotion_id}`}>
                    <FileText className="w-4 h-4 mr-1" /> Preview terms
                  </Button>
                  <Button size="sm" variant="outline" onClick={() => openEntries(p)}
                          className="border-slate-700 text-slate-300"
                          data-testid={`promo-entries-btn-${p.promotion_id}`}>
                    <Eye className="w-4 h-4 mr-1" /> Entries
                  </Button>
                </div>
              </div>
            </CardContent>
          </Card>
        );})}
      </div>

      {/* Create / Edit dialog — same form, mode driven by `editing` */}
      <PromotionFormDialog
        open={formOpen}
        promo={editing}
        services={services}
        onClose={closeForm}
        onSaved={load}
      />

      {/* Entries dialog */}
      <Dialog open={!!entriesFor} onOpenChange={(v) => { if (!v) { setEntriesFor(null); setRepairPlan(null); } }}>
        <DialogContent className="bg-slate-900 border-slate-700 text-white max-w-2xl" data-testid="promo-entries-dialog">
          <DialogHeader>
            <DialogTitle>Entries · {entriesFor?.name}</DialogTitle>
            <DialogDescription className="text-slate-400 text-xs">
              {entries.length} total. The euro figure is how much that customer was
              discounted; the balance still to pay is shown underneath.
            </DialogDescription>
          </DialogHeader>
          <div className="max-h-[400px] overflow-y-auto text-xs">
            {entries.length === 0 && <p className="text-slate-500 text-center py-4">No entries yet.</p>}
            {entries.map((e) => (
              <div key={e.entry_id} className="flex justify-between gap-3 py-1.5 border-b border-slate-800"
                   data-testid={`entry-row-${e.entry_id}`}>
                <span className="text-slate-300 truncate">
                  {e.customer_name || "—"} <span className="text-slate-500">· {e.booking_id}</span>
                </span>
                {e.outcome_label ? (
                  <span className="text-right whitespace-nowrap">
                    <span className="font-mono text-slate-100 block">
                      {e.outcome_label}
                      {e.discount_amount != null && <> · <span className="text-green-400">−€{e.discount_amount.toFixed(2)} saved</span></>}
                      {e.is_winner && " 🏆"}
                    </span>
                    {e.remaining_balance_due != null && (
                      <span className="text-[10px] text-slate-500 font-mono">
                        €{e.remaining_balance_due.toFixed(2)} left to pay
                      </span>
                    )}
                  </span>
                ) : (
                  // "pending draw" only makes sense for an undrawn
                  // jackpot. A blank promo_code entry is a leftover
                  // ghost from the old auto-entry bug — say so.
                  <span className="text-slate-500 whitespace-nowrap">
                    {entriesFor?.mechanic_type === "promo_code" ? "no discount recorded" : "pending draw"}
                  </span>
                )}
              </div>
            ))}
          </div>

          {/* promo_code integrity repair — dry-run first, then apply */}
          {entriesFor?.mechanic_type === "promo_code" && (
            <div className="border-t border-slate-800 pt-3 space-y-2" data-testid="promo-entries-repair">
              <p className="text-xs text-slate-500">
                Code used {entriesFor.uses_count ?? 0} time(s). If this list doesn't match
                that, check it — older bookings could log an entry without a redemption,
                or redeem without logging one.
              </p>
              {repairPlan && (
                <div className="rounded border border-slate-700 bg-slate-800/40 p-2 space-y-1 text-xs"
                     data-testid="promo-entries-repair-plan">
                  <p className="text-white">
                    {repairPlan.ghosts.length} to remove · {repairPlan.repairs.length} to
                    complete · {repairPlan.backfills.length} to add back
                  </p>
                  {repairPlan.ghosts.map((g) => (
                    <p key={g.entry_id} className="text-amber-300/80">
                      Remove: {g.customer_name || g.booking_id} (never used the code)
                    </p>
                  ))}
                  {repairPlan.backfills.map((b) => (
                    <p key={b.booking_id} className="text-green-300/80">
                      Add: {b.customer_name || b.booking_id} — {b.outcome_label}
                    </p>
                  ))}
                  {repairPlan.repairs.map((r) => (
                    <p key={r.entry_id} className="text-sky-300/80">
                      Complete: {r.customer_name || r.booking_id} — {r.outcome_label}
                    </p>
                  ))}
                </div>
              )}
              <div className="flex gap-2">
                <Button size="sm" variant="outline" disabled={repairBusy}
                        onClick={() => runEntriesRepair(true)}
                        className="border-slate-700 text-slate-300"
                        data-testid="promo-entries-check-btn">
                  Check this list
                </Button>
                <Button size="sm" disabled={repairBusy || !repairPlan
                          || (repairPlan.ghosts.length + repairPlan.repairs.length
                              + repairPlan.backfills.length) === 0}
                        onClick={() => runEntriesRepair(false)}
                        className="bg-green-600 hover:bg-green-700"
                        data-testid="promo-entries-fix-btn">
                  Fix it
                </Button>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Draw confirmation */}
      <Dialog open={!!drawTarget} onOpenChange={(v) => { if (!v && !drawSubmitting) setDrawTarget(null); }}>
        <DialogContent className="bg-slate-900 border-slate-700 text-white" data-testid="draw-confirm-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Trophy className="w-5 h-5 text-amber-400" /> Draw winner — irreversible
            </DialogTitle>
            <DialogDescription className="text-slate-300 text-sm">
              Picks one entry at random from <strong>{drawTarget?.entries_count}</strong> entries in <strong>{drawTarget?.name}</strong>.
              The winner&apos;s remaining balance becomes €0. This cannot be undone.
            </DialogDescription>
          </DialogHeader>
          <DialogFooter className="gap-2">
            <Button variant="outline" onClick={() => setDrawTarget(null)} disabled={drawSubmitting}
                    className="border-slate-700 text-slate-300" data-testid="draw-cancel-btn">Cancel</Button>
            <Button onClick={confirmDraw} disabled={drawSubmitting}
                    className="bg-amber-500 hover:bg-amber-600 text-slate-900 font-semibold"
                    data-testid="draw-confirm-btn">
              {drawSubmitting ? <Loader2 className="w-4 h-4 animate-spin" /> : "Confirm draw"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* End confirmation — terminal state, no undo */}
      <Dialog open={!!endTarget} onOpenChange={(v) => { if (!v && !endSubmitting) setEndTarget(null); }}>
        <DialogContent className="bg-slate-900 border-slate-700 text-white" data-testid="end-confirm-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Square className="w-5 h-5 text-slate-300" /> End campaign — cannot be resumed
            </DialogTitle>
            <DialogDescription className="text-slate-300 text-sm">
              This marks <strong>{endTarget?.name}</strong> as ended. No more entries will be accepted.
              Existing entries and payouts are kept for audit. If you might resume it later, use
              <span className="text-amber-300"> Pause</span> instead.
            </DialogDescription>
          </DialogHeader>
          <DialogFooter className="gap-2">
            <Button variant="outline" onClick={() => setEndTarget(null)} disabled={endSubmitting}
                    className="border-slate-700 text-slate-300" data-testid="end-cancel-btn">Cancel</Button>
            <Button onClick={confirmEnd} disabled={endSubmitting}
                    className="bg-slate-700 hover:bg-slate-600 text-white font-semibold"
                    data-testid="end-confirm-btn">
              {endSubmitting ? <Loader2 className="w-4 h-4 animate-spin" /> : "End campaign"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Reopen — ended promo_code campaigns only. Brings the code back
          with its history intact. Preconditions (expired end date,
          exhausted use cap) are answered IN this dialog. */}
      <Dialog open={!!reopenTarget}
              onOpenChange={(v) => { if (!v && !reopenSubmitting) setReopenTarget(null); }}>
        <DialogContent className="bg-slate-900 border-slate-700 text-white"
                       data-testid="reopen-confirm-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <RotateCcw className="w-5 h-5 text-emerald-400" /> Reopen campaign
            </DialogTitle>
            <DialogDescription className="text-slate-300 text-sm">
              Customers can use{" "}
              <strong className="text-amber-300">{reopenTarget?.code}</strong>{" "}
              again immediately. <strong>{reopenTarget?.name}</strong> keeps its{" "}
              {reopenTarget?.uses_count ?? 0} use(s) and{" "}
              {reopenTarget?.entries_count ?? 0} entry(ies) — nothing is reset.
            </DialogDescription>
          </DialogHeader>

          {reopenNeeds.includes("end_date_passed") && (
            <div className="space-y-2 text-sm" data-testid="reopen-end-date-block">
              <p className="text-amber-200 text-xs">
                Its end date has passed. Pick a new one, or remove the end date so
                the code runs until you end it again.
              </p>
              <Label className="text-slate-300 text-xs">New end date</Label>
              <Input
                type="datetime-local"
                value={reopenForm.new_end_date}
                disabled={reopenForm.clear_end_date}
                onChange={(e) => setReopenForm({ ...reopenForm, new_end_date: e.target.value })}
                className="bg-slate-800 border-slate-700 text-white"
                data-testid="reopen-new-end-date"
              />
              <label className="flex items-center gap-2 text-xs text-slate-300">
                <input type="checkbox" checked={reopenForm.clear_end_date}
                       onChange={(e) => setReopenForm({
                         ...reopenForm, clear_end_date: e.target.checked })}
                       data-testid="reopen-clear-end-date" />
                No end date — run until I end it
              </label>
            </div>
          )}

          {reopenNeeds.includes("max_uses_reached") && (
            <div className="space-y-2 text-sm" data-testid="reopen-max-uses-block">
              <p className="text-amber-200 text-xs">
                The code has been used all {reopenTarget?.max_uses} time(s). Raise
                the limit, or remove it.
              </p>
              <Label className="text-slate-300 text-xs">New use limit</Label>
              <Input
                type="number" min={(reopenTarget?.uses_count ?? 0) + 1}
                value={reopenForm.new_max_uses}
                disabled={reopenForm.clear_max_uses}
                onChange={(e) => setReopenForm({ ...reopenForm, new_max_uses: e.target.value })}
                className="bg-slate-800 border-slate-700 text-white"
                data-testid="reopen-new-max-uses"
              />
              <label className="flex items-center gap-2 text-xs text-slate-300">
                <input type="checkbox" checked={reopenForm.clear_max_uses}
                       onChange={(e) => setReopenForm({
                         ...reopenForm, clear_max_uses: e.target.checked })}
                       data-testid="reopen-clear-max-uses" />
                No limit — unlimited uses
              </label>
            </div>
          )}

          <DialogFooter className="gap-2">
            <Button variant="outline" onClick={() => setReopenTarget(null)}
                    disabled={reopenSubmitting}
                    className="border-slate-700 text-slate-300"
                    data-testid="reopen-cancel-btn">Cancel</Button>
            <Button onClick={confirmReopen} disabled={reopenSubmitting}
                    className="bg-emerald-600 hover:bg-emerald-500 text-white font-semibold"
                    data-testid="reopen-confirm-btn">
              {reopenSubmitting ? <Loader2 className="w-4 h-4 animate-spin" />
                : `Reopen ${reopenTarget?.code || "campaign"}`}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Delete confirmation — only visible when zero entries */}
      <Dialog open={!!deleteTarget} onOpenChange={(v) => { if (!v && !deleteSubmitting) setDeleteTarget(null); }}>
        <DialogContent className="bg-slate-900 border-slate-700 text-white" data-testid="delete-confirm-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Trash2 className="w-5 h-5 text-red-400" /> Delete campaign
            </DialogTitle>
            <DialogDescription className="text-slate-300 text-sm">
              Permanently removes <strong>{deleteTarget?.name}</strong>. Allowed because it has no entries.
              This cannot be undone.
            </DialogDescription>
          </DialogHeader>
          <DialogFooter className="gap-2">
            <Button variant="outline" onClick={() => setDeleteTarget(null)} disabled={deleteSubmitting}
                    className="border-slate-700 text-slate-300" data-testid="delete-cancel-btn">Cancel</Button>
            <Button onClick={confirmDelete} disabled={deleteSubmitting}
                    className="bg-red-600 hover:bg-red-500 text-white font-semibold"
                    data-testid="delete-confirm-btn">
              {deleteSubmitting ? <Loader2 className="w-4 h-4 animate-spin" /> : "Delete permanently"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
      {/* Terms preview dialog — renders the exact TermsBlock the
          public /promotion-terms page uses, driven by the admin
          terms-preview endpoint (auth-gated, any status). */}
      <Dialog open={!!termsPreviewFor} onOpenChange={(v) => { if (!v) { setTermsPreviewFor(null); setTermsPreview(null); } }}>
        <DialogContent className="bg-slate-950 border-slate-700 text-white max-w-2xl" data-testid="promo-terms-preview-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <FileText className="w-5 h-5 text-amber-400" />
              Terms preview · {termsPreviewFor?.name}
            </DialogTitle>
            <DialogDescription className="text-slate-400 text-xs">
              {termsPreview?.status === "active" ? (
                <span className="flex items-center gap-2 flex-wrap">
                  This campaign is <strong className="text-emerald-300">currently live</strong>.
                  <Link
                    to="/promotion-terms"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-amber-300 hover:text-amber-200 inline-flex items-center gap-1 underline"
                    data-testid="promo-terms-preview-live-link"
                  >
                    See the public terms page <ExternalLink className="w-3 h-3" />
                  </Link>
                </span>
              ) : (
                <>Same template a customer sees when the campaign is live.
                Status: <strong>{termsPreview?.status || "…"}</strong>.</>
              )}
            </DialogDescription>
          </DialogHeader>
          <div className="max-h-[65vh] overflow-y-auto">
            {termsPreview ? (
              <TermsBlock campaign={termsPreview} />
            ) : (
              <div className="py-8 text-center text-slate-500">
                <Loader2 className="w-4 h-4 animate-spin inline-block" /> Loading…
              </div>
            )}
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
};

// --- Create/Edit dialog ---------------------------------------------------
// One form for both modes: `promo === null` → create, else prefilled edit.
// Mechanic is NOT editable in edit mode (would silently switch the entry
// pool's semantics — safer to force delete + recreate for that).
const PromotionFormDialog = ({ open, promo, services = [], onClose, onSaved }) => {
  const isEdit = !!promo;
  const [name, setName] = useState("");
  const [mechanicType, setMechanicType] = useState("weighted_random");
  const [maxEntries, setMaxEntries] = useState("50");
  const [startDate, setStartDate] = useState("");
  const [endDate, setEndDate] = useState("");
  const [prizes, setPrizes] = useState([
    { label: "10% off", discount_type: "pct", value: 10, weight: 60 },
    { label: "Free service", discount_type: "free", value: 0, weight: 1 },
  ]);
  // single_winner (jackpot) prize — the whole campaign shares one
  // fixed prize, not a table.
  const [jackpotType, setJackpotType] = useState("free");
  const [jackpotValue, setJackpotValue] = useState("");
  // promo_code fields — same discount vocabulary as prize slots, but
  // paired with a shareable code + optional max_uses cap. No prize
  // table, no wheel, no draw — this mechanic bypasses those entirely.
  const [code, setCode] = useState("");
  const [codeDiscountType, setCodeDiscountType] = useState("pct");
  const [codeDiscountValue, setCodeDiscountValue] = useState("");
  const [codeMaxUses, setCodeMaxUses] = useState(""); // empty = unlimited
  // Services the campaign scopes to. Empty array = ALL services
  // (backend interpretation). Applies to every mechanic type.
  const [applicableServiceIds, setApplicableServiceIds] = useState([]);
  const [additionalTerms, setAdditionalTerms] = useState("");
  // free_wash fields — the wash is bought from an external company, so
  // the cost is entered NET with VAT on top (same custom-rate input as
  // the Finance categories). `customer_text` is the only one a customer
  // ever sees.
  const [washCostNet, setWashCostNet] = useState("12.00");
  const [washVatTreatment, setWashVatTreatment] = useState("zero");
  const [washVatRate, setWashVatRate] = useState("");
  const [washCustomerText, setWashCustomerText] = useState("Free car wash included");
  const [submitting, setSubmitting] = useState(false);

  // Rehydrate whenever the dialog opens for a different promo.
  useEffect(() => {
    if (!open) return;
    if (promo) {
      setName(promo.name || "");
      setMechanicType(promo.mechanic_type || "weighted_random");
      setMaxEntries(String(promo.max_entries ?? 50));
      setStartDate(isoToLocalInput(promo.start_date));
      setEndDate(isoToLocalInput(promo.end_date));
      setPrizes(
        (promo.prize_table && promo.prize_table.length > 0)
          ? promo.prize_table.map((p) => ({
              label: p.label || "", discount_type: p.discount_type || "pct",
              value: p.value ?? 0, weight: p.weight ?? 1,
            }))
          : [
              { label: "10% off", discount_type: "pct", value: 10, weight: 60 },
              { label: "Free service", discount_type: "free", value: 0, weight: 1 },
            ]
      );
      setJackpotType(promo.prize_discount_type || "free");
      setJackpotValue(
        promo.prize_discount_value != null ? String(promo.prize_discount_value) : ""
      );
      setCode(promo.code || "");
      setWashCostNet(promo.wash_cost_net != null ? String(promo.wash_cost_net) : "12.00");
      setWashVatTreatment(promo.wash_vat_treatment || "zero");
      setWashVatRate(
        promo.wash_vat_rate_percent != null ? String(promo.wash_vat_rate_percent) : ""
      );
      setWashCustomerText(promo.customer_text || "Free car wash included");
      setCodeDiscountType(promo.discount_type || "pct");
      setCodeDiscountValue(
        promo.discount_value != null ? String(promo.discount_value) : ""
      );
      setCodeMaxUses(
        promo.max_uses != null ? String(promo.max_uses) : ""
      );
      setApplicableServiceIds(
        Array.isArray(promo.applicable_service_ids)
          ? promo.applicable_service_ids
          : []
      );
      setAdditionalTerms(promo.additional_terms || "");
    } else {
      setName(""); setMechanicType("weighted_random"); setMaxEntries("50");
      setStartDate(""); setEndDate("");
      setPrizes([
        { label: "10% off", discount_type: "pct", value: 10, weight: 60 },
        { label: "Free service", discount_type: "free", value: 0, weight: 1 },
      ]);
      setJackpotType("free");
      setJackpotValue("");
      setCode("");
      setCodeDiscountType("pct");
      setCodeDiscountValue("");
      setCodeMaxUses("");
      setApplicableServiceIds([]);
      setAdditionalTerms("");
      setWashCostNet("12.00");
      setWashVatTreatment("zero");
      setWashVatRate("");
      setWashCustomerText("Free car wash included");
    }
  }, [open, promo]);

  const submit = async () => {
    const washMechanic = mechanicType === "free_wash";
    if (!name || !startDate || (!endDate && !washMechanic)) {
      toast.error("Name, start and end dates are required");
      return;
    }
    if (washMechanic) {
      const n = Number(washCostNet);
      if (!Number.isFinite(n) || n <= 0) {
        toast.error("Enter the net price the wash company charges (e.g. 12.00)");
        return;
      }
      if (washVatTreatment === "custom") {
        const r = Number(washVatRate);
        if (!Number.isFinite(r) || r < 0 || r > 100) {
          toast.error("Enter a custom VAT rate between 0 and 100");
          return;
        }
      }
    }
    if (mechanicType === "single_winner" && jackpotType !== "free") {
      const n = Number(jackpotValue);
      if (!Number.isFinite(n) || n <= 0) {
        toast.error("Enter a prize value (or set the prize to Free)");
        return;
      }
    }
    if (mechanicType === "promo_code") {
      if (!code.trim()) {
        toast.error("Enter a promo code");
        return;
      }
      if (codeDiscountType !== "free") {
        const n = Number(codeDiscountValue);
        if (!Number.isFinite(n) || n <= 0) {
          toast.error("Enter a discount value (or set the discount to Free)");
          return;
        }
      }
      if (codeMaxUses !== "" && codeMaxUses !== null) {
        const m = Number(codeMaxUses);
        if (!Number.isInteger(m) || m < 1) {
          toast.error("Max uses must be a whole number ≥ 1 (or leave blank for unlimited)");
          return;
        }
      }
    }
    setSubmitting(true);
    try {
      const body = {
        name,
        // max_entries is required by the Promotion model but meaningless
        // for promo_code — the real cap is max_uses. The backend also
        // force-normalises this to 1 for promo_code campaigns
        // (routes/promotions.py L408-411), so we mirror that sentinel
        // here rather than shipping a hidden-field value the user never
        // saw. For wheel/jackpot, ship whatever the (now-visible) input
        // holds.
        max_entries:
          mechanicType === "promo_code" || mechanicType === "free_wash"
            ? 1 : Number(maxEntries),
        start_date: new Date(startDate).toISOString(),
        // A standing wash rule may run until the owner ends it.
        end_date: endDate ? new Date(endDate).toISOString() : null,
        additional_terms: additionalTerms.trim() || null,
        // Empty list = ALL services (backend query treats null / []
        // identically at routes/promotions.py L211-214). We always
        // ship the field so an edit can widen a previously-scoped
        // campaign back to "all" by clearing the picker.
        applicable_service_ids: applicableServiceIds,
      };
      if (mechanicType === "weighted_random") {
        body.prize_table = prizes.map((p) => ({
          label: p.label, discount_type: p.discount_type,
          value: Number(p.value) || 0, weight: Math.max(1, Number(p.weight) || 1),
        }));
      } else if (mechanicType === "free_wash") {
        body.wash_cost_net = Number(washCostNet);
        body.wash_vat_treatment = washVatTreatment;
        body.wash_vat_rate_percent =
          washVatTreatment === "custom" ? Number(washVatRate) : null;
        body.customer_text = washCustomerText.trim() || null;
      } else if (mechanicType === "single_winner") {
        body.prize_discount_type = jackpotType;
        body.prize_discount_value =
          jackpotType === "free" ? 0 : Number(jackpotValue);
      } else {
        // promo_code — code + discount + optional max_uses cap.
        body.code = code.trim().toUpperCase();
        body.discount_type = codeDiscountType;
        body.discount_value =
          codeDiscountType === "free" ? 0 : Number(codeDiscountValue);
        body.max_uses =
          codeMaxUses === "" || codeMaxUses === null
            ? null
            : Number(codeMaxUses);
      }
      if (isEdit) {
        await axios.patch(`${API}/promotions/${promo.promotion_id}`, body);
        if (mechanicType === "free_wash") {
          // Price + VAT are editable in ANY status, so they travel on
          // their own audited endpoint rather than the general edit
          // (which is locked once a campaign has entries).
          await axios.patch(`${API}/promotions/${promo.promotion_id}/wash-pricing`, {
            wash_cost_net: Number(washCostNet),
            wash_vat_treatment: washVatTreatment,
            wash_vat_rate_percent:
              washVatTreatment === "custom" ? Number(washVatRate) : null,
          });
        }
        toast.success("Campaign updated.");
      } else {
        body.mechanic_type = mechanicType;
        const created = await axios.post(`${API}/promotions/`, body);
        toast.success("Campaign created in draft — activate when ready.");
        // A code released by an ENDED campaign can be re-used. Say where
        // the old history lives so nobody thinks it was moved or lost.
        if (created.data?.ended_namesake?.message) {
          toast.info(created.data.ended_namesake.message, { duration: 8000 });
        }
      }
      onSaved(); onClose();
    } catch (err) {
      toast.error(err.response?.data?.detail || (isEdit ? "Failed to save" : "Failed to create"));
    } finally { setSubmitting(false); }
  };

  return (
    <Dialog open={open} onOpenChange={(v) => { if (!v) onClose(); }}>
      <DialogContent className="bg-slate-900 border-slate-700 text-white max-w-lg" data-testid="promo-form-dialog">
        <DialogHeader>
          <DialogTitle>{isEdit ? `Edit campaign · ${promo?.name}` : "New promotion campaign"}</DialogTitle>
          <DialogDescription className="text-slate-400 text-xs">
            {isEdit
              ? "Editable while status is draft OR the campaign has zero entries. Mechanic can't be swapped — delete and recreate to change mechanic."
              : "Created in draft — activate when you're ready to go live. Weights don't need to sum to 100."}
          </DialogDescription>
        </DialogHeader>
        <div className="space-y-3 max-h-[60vh] overflow-y-auto">
          <div>
            <Label className="text-slate-300 text-xs">Name</Label>
            <Input value={name} onChange={(e) => setName(e.target.value)}
                   placeholder="e.g. Spring Wheel" className="bg-slate-800 border-slate-700 text-white"
                   data-testid="promo-form-name" />
          </div>
          <div className="grid grid-cols-2 gap-2">
            <div>
              <Label className="text-slate-300 text-xs">Mechanic</Label>
              <Select value={mechanicType} onValueChange={setMechanicType} disabled={isEdit}>
                <SelectTrigger className="bg-slate-800 border-slate-700 text-white disabled:opacity-60"
                               data-testid="promo-form-mechanic"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="weighted_random">Weighted random (wheel)</SelectItem>
                  <SelectItem value="single_winner">Single winner (jackpot)</SelectItem>
                  <SelectItem value="promo_code">Promo code (word-of-mouth)</SelectItem>
                  <SelectItem value="free_wash">Free car wash (perk)</SelectItem>
                </SelectContent>
              </Select>
            </div>
            {mechanicType !== "promo_code" && mechanicType !== "free_wash" && (
              <div>
                <Label className="text-slate-300 text-xs">Max entries</Label>
                <Input type="number" min="1" value={maxEntries}
                       onChange={(e) => setMaxEntries(e.target.value)}
                       className="bg-slate-800 border-slate-700 text-white"
                       data-testid="promo-form-max" />
              </div>
            )}
          </div>
          <div className="grid grid-cols-2 gap-2">
            <div>
              <Label className="text-slate-300 text-xs">Start</Label>
              <Input type="datetime-local" value={startDate}
                     onChange={(e) => setStartDate(e.target.value)}
                     className="bg-slate-800 border-slate-700 text-white"
                     data-testid="promo-form-start" />
            </div>
            <div>
              <Label className="text-slate-300 text-xs">End</Label>
              <Input type="datetime-local" value={endDate}
                     onChange={(e) => setEndDate(e.target.value)}
                     className="bg-slate-800 border-slate-700 text-white"
                     data-testid="promo-form-end" />
              {mechanicType === "free_wash" && (
                <p className="text-[11px] text-slate-500 mt-1">
                  Leave blank for a standing rule with no end date.
                </p>
              )}
            </div>
          </div>

          {mechanicType === "free_wash" && (
            <div className="border border-cyan-800/60 rounded-md p-3 space-y-2"
                 data-testid="promo-form-wash-block">
              <Label className="text-slate-300 text-xs">
                What the wash costs us (admin only — never shown to customers)
              </Label>
              <div className="grid grid-cols-2 gap-2">
                <div>
                  <Label className="text-slate-500 text-[11px]">Net price (€)</Label>
                  <Input type="number" min="0" step="0.01" value={washCostNet}
                         onChange={(e) => setWashCostNet(e.target.value)}
                         className="bg-slate-800 border-slate-700 text-white"
                         data-testid="promo-form-wash-cost" />
                </div>
                <div>
                  <Label className="text-slate-500 text-[11px]">VAT</Label>
                  <Select value={washVatTreatment} onValueChange={setWashVatTreatment}>
                    <SelectTrigger className="bg-slate-800 border-slate-700 text-white text-xs"
                                   data-testid="promo-form-wash-vat"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="zero">0% (zero rated)</SelectItem>
                      <SelectItem value="no_vat">No VAT</SelectItem>
                      <SelectItem value="standard_23">23%</SelectItem>
                      <SelectItem value="standard_9">9%</SelectItem>
                      <SelectItem value="reduced_13_5">13.5%</SelectItem>
                      <SelectItem value="custom">Custom rate…</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </div>
              {washVatTreatment === "custom" && (
                <div>
                  <Label className="text-slate-500 text-[11px]">Custom VAT rate (%)</Label>
                  <Input type="number" min="0" max="100" step="0.01" value={washVatRate}
                         onChange={(e) => setWashVatRate(e.target.value)}
                         placeholder="e.g. 9"
                         className="bg-slate-800 border-slate-700 text-white"
                         data-testid="promo-form-wash-vat-rate" />
                </div>
              )}
              <p className="text-[11px] text-slate-500">
                VAT counts as reclaimable only above 0%. A price change applies
                to jobs completed from then on — cost lines already written on
                completed jobs never change.
              </p>
              <div>
                <Label className="text-slate-500 text-[11px]">
                  Customer-facing text
                </Label>
                <Input value={washCustomerText}
                       onChange={(e) => setWashCustomerText(e.target.value)}
                       placeholder="Free car wash included"
                       className="bg-slate-800 border-slate-700 text-white"
                       data-testid="promo-form-wash-text" />
              </div>
              {isEdit && (promo?.wash_price_history || []).length > 0 && (
                <div className="pt-2 border-t border-slate-800"
                     data-testid="promo-form-wash-history">
                  <p className="text-[10px] uppercase tracking-wide text-slate-500 mb-1">
                    Price history
                  </p>
                  {(promo.wash_price_history || []).slice().reverse().map((h, i) => (
                    <p key={i} className="text-[11px] text-slate-400">
                      {String(h.at).slice(0, 16).replace("T", " ")} · {h.by} ·
                      {" "}€{Number(h.from?.wash_cost_net ?? 0).toFixed(2)} @{" "}
                      {Number(h.from?.wash_vat_rate_percent ?? 0)}% → €
                      {Number(h.to?.wash_cost_net ?? 0).toFixed(2)} @{" "}
                      {Number(h.to?.wash_vat_rate_percent ?? 0)}%
                    </p>
                  ))}
                </div>
              )}
            </div>
          )}

          {/* Applies to — service scope picker. Empty list = ALL
              services (matches the backend's null/[] interpretation
              at routes/promotions.py :: enter_promotion_for_booking).
              Rendered for every mechanic type since scope is a shared
              concept. Grouped by category so 23 services stay
              scannable. */}
          <div
            className="border border-slate-800 rounded-md p-3 space-y-2"
            data-testid="promo-form-applies-block"
          >
            <div className="flex items-center justify-between">
              <Label className="text-slate-300 text-xs">
                Applies to (leave empty for all services)
              </Label>
              {applicableServiceIds.length > 0 && (
                <button
                  type="button"
                  onClick={() => setApplicableServiceIds([])}
                  className="text-[11px] text-slate-400 hover:text-amber-300 underline"
                  data-testid="promo-form-applies-clear"
                >
                  Clear (= all services)
                </button>
              )}
            </div>
            <p className="text-[11px] text-slate-500" data-testid="promo-form-applies-summary">
              {applicableServiceIds.length === 0
                ? "All services — this campaign applies to every service."
                : `Scoped to ${applicableServiceIds.length} service${applicableServiceIds.length === 1 ? "" : "s"}.`}
            </p>
            {services.length === 0 ? (
              <p className="text-[11px] text-slate-500 italic">
                Services list unavailable — save leaves this as “all services”.
              </p>
            ) : (
              <div
                className="max-h-56 overflow-y-auto pr-1 space-y-1"
                data-testid="promo-form-applies-list"
              >
                {Object.entries(
                  services.reduce((acc, s) => {
                    const cat = s.category || "Other";
                    (acc[cat] = acc[cat] || []).push(s);
                    return acc;
                  }, {}),
                ).map(([cat, list]) => (
                  <div key={cat} className="pt-1">
                    <div className="text-[10px] uppercase tracking-wide text-slate-500 mb-1">
                      {cat}
                    </div>
                    {list.map((s) => {
                      const checked = applicableServiceIds.includes(s.service_id);
                      return (
                        <label
                          key={s.service_id}
                          className="flex items-center gap-2 text-xs text-slate-300 py-0.5 cursor-pointer hover:text-white"
                          data-testid={`promo-form-applies-row-${s.service_id}`}
                        >
                          <input
                            type="checkbox"
                            checked={checked}
                            onChange={(e) => {
                              setApplicableServiceIds((prev) =>
                                e.target.checked
                                  ? [...prev, s.service_id]
                                  : prev.filter((id) => id !== s.service_id),
                              );
                            }}
                            className="accent-saturn-orange"
                            data-testid={`promo-form-applies-checkbox-${s.service_id}`}
                          />
                          <span>{s.name}</span>
                        </label>
                      );
                    })}
                  </div>
                ))}
              </div>
            )}
          </div>

          {mechanicType === "single_winner" && (
            <div className="border border-slate-800 rounded-md p-3 space-y-2"
                 data-testid="promo-form-jackpot-prize">
              <Label className="text-slate-300 text-xs">Jackpot prize (single winner)</Label>
              <div className="grid grid-cols-2 gap-2">
                <Select value={jackpotType} onValueChange={setJackpotType}>
                  <SelectTrigger className="bg-slate-800 border-slate-700 text-white text-xs"
                                 data-testid="promo-form-jackpot-type"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="free">Free service</SelectItem>
                    <SelectItem value="pct">% off</SelectItem>
                    <SelectItem value="amount">€ off</SelectItem>
                  </SelectContent>
                </Select>
                {jackpotType !== "free" && (
                  <Input
                    type="number" min="0" step="0.01" value={jackpotValue}
                    onChange={(e) => setJackpotValue(e.target.value)}
                    placeholder={jackpotType === "pct" ? "e.g. 25" : "e.g. 50"}
                    className="bg-slate-800 border-slate-700 text-white text-xs"
                    data-testid="promo-form-jackpot-value"
                  />
                )}
              </div>
              <p className="text-[11px] text-amber-300" data-testid="promo-form-jackpot-preview">
                Winner receives:{" "}
                {jackpotType === "free" && "Free service"}
                {jackpotType === "pct" && `${Number(jackpotValue) || 0}% off`}
                {jackpotType === "amount" && `€${Number(jackpotValue) || 0} off`}
              </p>
              <p className="text-[11px] text-slate-500">
                Same math as the wheel: percentage is off the full service price, floored at zero. Your deposit is never refunded.
              </p>
            </div>
          )}

          {mechanicType === "weighted_random" && (
            <div>
              <div className="flex items-center justify-between">
                <Label className="text-slate-300 text-xs">Prize table</Label>
                <Button size="sm" variant="ghost" onClick={() => setPrizes([...prizes, { label: "", discount_type: "pct", value: 5, weight: 10 }])}
                        className="text-amber-300 hover:text-amber-200"
                        data-testid="promo-form-add-prize">
                  <Plus className="w-3 h-3 mr-1" /> Add slot
                </Button>
              </div>
              <div className="space-y-1 mt-1">
                {prizes.map((p, idx) => (
                  <div key={idx} className="grid grid-cols-12 gap-1 items-center"
                       data-testid={`promo-form-prize-row-${idx}`}>
                    <Input value={p.label} onChange={(e) => {
                      const c = [...prizes]; c[idx].label = e.target.value; setPrizes(c);
                    }} placeholder="Label" className="col-span-4 bg-slate-800 border-slate-700 text-white text-xs" />
                    <Select value={p.discount_type} onValueChange={(v) => {
                      const c = [...prizes]; c[idx].discount_type = v; setPrizes(c);
                    }}>
                      <SelectTrigger className="col-span-3 bg-slate-800 border-slate-700 text-white text-xs">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="pct">%</SelectItem>
                        <SelectItem value="amount">€</SelectItem>
                        <SelectItem value="free">Free</SelectItem>
                      </SelectContent>
                    </Select>
                    <Input type="number" value={p.value} onChange={(e) => {
                      const c = [...prizes]; c[idx].value = e.target.value; setPrizes(c);
                    }} className="col-span-2 bg-slate-800 border-slate-700 text-white text-xs" placeholder="Val" />
                    <Input type="number" min="1" value={p.weight} onChange={(e) => {
                      const c = [...prizes]; c[idx].weight = e.target.value; setPrizes(c);
                    }} className="col-span-2 bg-slate-800 border-slate-700 text-white text-xs" placeholder="Wgt" />
                    <Button size="sm" variant="ghost" onClick={() => setPrizes(prizes.filter((_, i) => i !== idx))}
                            className="col-span-1 text-slate-500 hover:text-red-400"
                            data-testid={`promo-form-remove-prize-${idx}`}>
                      <X className="w-3 h-3" />
                    </Button>
                  </div>
                ))}
              </div>
              <p className="text-[11px] text-slate-500 mt-2">
                Weight is relative — a slot with weight 1 next to weight 99 shows up ~1% of the time.
              </p>
            </div>
          )}

          {mechanicType === "promo_code" && (
            <div className="border border-slate-800 rounded-md p-3 space-y-2"
                 data-testid="promo-form-code-block">
              <Label className="text-slate-300 text-xs">
                Shareable code (customer types this on the booking form)
              </Label>
              <Input
                value={code}
                onChange={(e) => setCode(e.target.value.toUpperCase())}
                placeholder="e.g. SATURN20"
                className="bg-slate-800 border-slate-700 text-white uppercase"
                data-testid="promo-form-code"
                disabled={isEdit}
                title={isEdit ? "Code can't change after creation" : ""}
              />
              <div className="grid grid-cols-2 gap-2">
                <div>
                  <Label className="text-slate-300 text-xs">Discount</Label>
                  <Select value={codeDiscountType} onValueChange={setCodeDiscountType}>
                    <SelectTrigger className="bg-slate-800 border-slate-700 text-white text-xs"
                                   data-testid="promo-form-code-discount-type"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="pct">% off</SelectItem>
                      <SelectItem value="amount">€ off</SelectItem>
                      <SelectItem value="free">Free service</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                {codeDiscountType !== "free" && (
                  <div>
                    <Label className="text-slate-300 text-xs">Value</Label>
                    <Input
                      type="number" min="0" step="0.01" value={codeDiscountValue}
                      onChange={(e) => setCodeDiscountValue(e.target.value)}
                      placeholder={codeDiscountType === "pct" ? "e.g. 20" : "e.g. 40"}
                      className="bg-slate-800 border-slate-700 text-white text-xs"
                      data-testid="promo-form-code-discount-value"
                    />
                  </div>
                )}
              </div>
              <div>
                <Label className="text-slate-300 text-xs">Max uses (optional)</Label>
                <Input
                  type="number" min="1" value={codeMaxUses}
                  onChange={(e) => setCodeMaxUses(e.target.value)}
                  placeholder="Leave blank for unlimited"
                  className="bg-slate-800 border-slate-700 text-white text-xs"
                  data-testid="promo-form-code-max-uses"
                />
              </div>
              <p className="text-[11px] text-amber-300" data-testid="promo-form-code-preview">
                Customer gets:{" "}
                {codeDiscountType === "free" && "Free service"}
                {codeDiscountType === "pct" && `${Number(codeDiscountValue) || 0}% off`}
                {codeDiscountType === "amount" && `€${Number(codeDiscountValue) || 0} off`}
              </p>
              <p className="text-[11px] text-slate-500">
                Promo codes are word-of-mouth only — no public banner or terms page. Applying a code opts the customer OUT of any active wheel/jackpot for that booking.
              </p>
            </div>
          )}

          <div className="pt-2 border-t border-slate-800">
            <Label className="text-slate-300 text-xs">
              Additional terms (optional)
            </Label>
            <Textarea
              value={additionalTerms}
              onChange={(e) => setAdditionalTerms(e.target.value)}
              placeholder="e.g. Limited to one entry per household. Winner must be over 18."
              rows={3}
              className="bg-slate-800 border-slate-700 text-white text-xs mt-1"
              data-testid="promo-form-additional-terms"
            />
            <p className="text-[11px] text-slate-500 mt-1">
              Appended <strong>after</strong> the standard 5-point terms (deposit rule, floor-at-zero, no-cash-alternative etc). Doesn&apos;t replace them.
            </p>
          </div>
        </div>
        <DialogFooter className="gap-2">
          <Button variant="outline" onClick={onClose} disabled={submitting}
                  className="border-slate-700 text-slate-300"
                  data-testid="promo-form-cancel">Cancel</Button>
          <Button onClick={submit} disabled={submitting}
                  className="bg-amber-500 hover:bg-amber-600 text-slate-900 font-semibold"
                  data-testid="promo-form-submit">
            {submitting ? <Loader2 className="w-4 h-4 animate-spin" /> : (isEdit ? "Save changes" : "Create draft")}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default PromotionsTab;
